import { useQuery } from '@apollo/client';
import { GET_FEATURE_WIZARD_FUNNEL } from '../../../graphql/queries';
import { fieldText } from './draft';
import type { WizardAudienceFunnel, WizardFunnel, WizardStepFunnel } from './types';

/** A count or rate the control audience cannot have. Never rendered as a zero. */
const DASH = '—';

function count(value: number | null): string {
  return value === null ? DASH : value.toLocaleString();
}

function percent(value: number | null): string {
  return value === null ? DASH : `${Math.round(value * 100)}%`;
}

type MeasureRow = { label: string; wizard: string; control: string };

function measureRows(wizard: WizardAudienceFunnel, control: WizardAudienceFunnel): MeasureRow[] {
  return [
    { label: 'Arrived', wizard: count(wizard.arrivedCount), control: count(control.arrivedCount) },
    { label: 'Opened', wizard: count(wizard.openedCount), control: count(control.openedCount) },
    { label: 'Completed', wizard: count(wizard.completedCount), control: count(control.completedCount) },
    { label: 'Dismissed', wizard: count(wizard.dismissedCount), control: count(control.dismissedCount) },
    { label: 'Open rate', wizard: percent(wizard.openRate), control: percent(control.openRate) },
    { label: 'Completion rate', wizard: percent(wizard.completionRate), control: percent(control.completionRate) },
    { label: 'Dismiss rate', wizard: percent(wizard.dismissRate), control: percent(control.dismissRate) },
    { label: 'Finish-or-dismiss rate', wizard: percent(wizard.terminalRate), control: percent(control.terminalRate) },
  ];
}

const HEAD_CELL = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500';

function MeasureTable({ funnel }: { funnel: WizardFunnel }) {
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className={HEAD_CELL}>Measure</th>
          <th className={HEAD_CELL}>Wizard audience</th>
          <th className={HEAD_CELL}>Control audience</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {measureRows(funnel.wizardAudience, funnel.controlAudience).map((row) => (
          <tr key={row.label}>
            <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{row.label}</td>
            <td className="whitespace-nowrap px-6 py-3 text-sm font-medium text-gray-900">{row.wizard}</td>
            <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{row.control}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One row per authored position, however many there are — never a fixed three. */
function StepTable({ steps }: { steps: WizardStepFunnel[] }) {
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className={HEAD_CELL}>Step</th>
          <th className={HEAD_CELL}>Headline now at this position</th>
          <th className={HEAD_CELL}>Viewed</th>
          <th className={HEAD_CELL}>Submitted</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {steps.map((step) => (
          <tr key={step.stepPosition}>
            <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">Step {step.stepPosition}</td>
            <td className="px-6 py-3 text-sm text-gray-900">{fieldText(step.config, 'headline') || DASH}</td>
            <td className="whitespace-nowrap px-6 py-3 text-sm font-medium text-gray-900">{count(step.viewedCount)}</td>
            <td className="whitespace-nowrap px-6 py-3 text-sm font-medium text-gray-900">
              {count(step.submittedCount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The wizard's results. Everything here is the SECONDARY measure — the callout says so, and
 * names the exact Amplitude event and user property the headline rate is read from instead.
 */
export function WizardResults({ wizardId }: { wizardId: string }) {
  const { data, loading, error } = useQuery<{ featureWizardFunnel: WizardFunnel }>(GET_FEATURE_WIZARD_FUNNEL, {
    variables: { wizardId },
    fetchPolicy: 'cache-and-network',
  });

  const funnel = data?.featureWizardFunnel;

  if (error) return <p className="text-sm text-red-600">{error.message}</p>;
  if (!funnel) return <div className="text-gray-500">{loading ? 'Loading results…' : 'No results yet.'}</div>;

  const wizardShare = 100 - funnel.controlPercent;

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-lg font-semibold">{funnel.slug}</h3>
        <p className="mt-1 text-sm leading-relaxed text-gray-600">
          Measurement split: {wizardShare}% receive the wizard and {funnel.controlPercent}% are held out in the control
          audience. Total reach {funnel.totalReach.toLocaleString()} — everyone the link put in front of this wizard,
          across both audiences, and the only number that answers how many people the link actually reached.
        </p>
        <p className="mt-3 border-l-2 border-amber-300 pl-3 text-sm leading-relaxed text-amber-800">
          This funnel is the secondary open-to-complete measure, so a completion count here is not the feature-action
          metric. Read the headline per-wizard feature-action rate in Amplitude instead, by segmenting this
          wizard&rsquo;s target event <code className="font-mono">{funnel.targetEventName}</code> on the user property{' '}
          <code className="font-mono">{funnel.audienceProperty}</code>, which every arrival stamps on the person.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <MeasureTable funnel={funnel} />
        </div>
        <p className="px-6 py-4 text-xs leading-relaxed text-gray-500">
          Open rate is opened over arrived; completion, dismiss and finish-or-dismiss rates are all over opened, where
          finish-or-dismiss is the share of opened wizards that reached any terminal outcome rather than being left
          hanging. A rate reads as a dash, never a zero, when nothing reached its denominator. The dashes down the
          control column are by design: a held-out person is never shown a wizard to open, so control reports arrivals
          only.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <StepTable steps={funnel.steps} />
        </div>
        <p className="px-6 py-4 text-xs leading-relaxed text-gray-500">
          One row per authored position, counting distinct people — viewing a step twice counts once. A step&rsquo;s
          identity is its position and a wizard has no versions, so editing or reordering a published wizard&rsquo;s
          steps re-labels these historic per-step counts: they are comparable only while the step list is stable. The
          arrival, open, completion and dismissal counts above are unaffected by any edit.
        </p>
      </div>
    </div>
  );
}
